<script setup lang="ts" generic="T extends string | number">
// Sierra's .checks: a short list of pill checkboxes for picking several (weekdays,
// permissions). A checked pill takes the accent, through :has(input:checked).
// `label` is the group's legend. An option's `hint` shows as a muted line in its pill.
defineProps<{ modelValue: readonly T[]; options: readonly { value: T; label: string; hint?: string }[]; label: string; disabled?: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [value: T[]] }>();
</script>

<template>
  <fieldset class="itc-check-pills" :disabled="disabled">
    <legend class="itc-field-label">{{ label }}</legend>
    <div class="itc-check-pills__row">
      <label v-for="option in options" :key="option.value" class="itc-check-pills__pill" :class="{ 'itc-check-pills__pill--hint': option.hint }">
        <input
          type="checkbox"
          :checked="modelValue.includes(option.value)"
          @change="emit('update:modelValue', ($event.target as HTMLInputElement).checked ? [...modelValue, option.value] : modelValue.filter((value) => value !== option.value))"
        />
        <span v-if="option.hint" class="itc-check-pills__text">{{ option.label }}<small>{{ option.hint }}</small></span>
        <template v-else>{{ option.label }}</template>
      </label>
    </div>
  </fieldset>
</template>

<style scoped>
.itc-check-pills {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.itc-check-pills legend {
  margin-bottom: 5px;
  padding: 0;
}
.itc-check-pills__row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.itc-check-pills__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-pill);
  background: var(--itc-panel-2);
  font-size: 14px;
  cursor: pointer;
}
.itc-check-pills__pill:has(input:checked) {
  border-color: var(--itc-accent);
  background: var(--itc-accent-soft);
}
.itc-check-pills__pill--hint {
  align-items: flex-start;
  padding: 8px 14px;
  border-radius: var(--itc-r-control);
}
.itc-check-pills__pill--hint input {
  margin-top: 2px;
}
.itc-check-pills__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.itc-check-pills__text small {
  color: var(--itc-muted);
  font-size: 13px;
}
.itc-check-pills__pill input {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--itc-accent);
}
.itc-check-pills:disabled .itc-check-pills__pill {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
